import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Calendar,
  Clock,
  Users,
  MapPin,
  Video,
  ArrowLeft,
  Loader,
  CheckCircle,
  User,
} from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import toast from "react-hot-toast";
import { useAuth } from "../../contexts/AuthContext";

const GroupSessionDetails = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);

  useEffect(() => {
    fetchSessionDetails();
  }, [sessionId]);

  const fetchSessionDetails = async () => {
    try {
      setLoading(true);
      const API_URL =
        process.env.REACT_APP_API_URL || "http://localhost:5000/api/v1";
      const response = await axios.get(
        `${API_URL}/group-sessions/${sessionId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data.status === "success") {
        const sessionData = response.data.data;
        setSession(sessionData);

        // Check if user is already registered
        const userRegistered = sessionData.participants.some(
          (p) => p.user._id === user.id || p.user === user.id
        );
        setIsRegistered(userRegistered);
      }
    } catch (err) {
      console.error("Failed to fetch session:", err);
      toast.error("Failed to load session details");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    try {
      setRegistering(true);
      const API_URL =
        process.env.REACT_APP_API_URL || "http://localhost:5000/api/v1";
      const response = await axios.post(
        `${API_URL}/group-sessions/${sessionId}/register`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data.status === "success") {
        toast.success("Successfully registered for the session!");
        setIsRegistered(true);
        fetchSessionDetails(); // Refresh to update participant count
      }
    } catch (err) {
      console.error("Registration failed:", err);
      toast.error(err.response?.data?.message || "Failed to register");
    } finally {
      setRegistering(false);
    }
  };

  const handleCancelRegistration = async () => {
    try {
      setRegistering(true);
      const API_URL =
        process.env.REACT_APP_API_URL || "http://localhost:5000/api/v1";
      const response = await axios.delete(
        `${API_URL}/group-sessions/${sessionId}/register`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data.status === "success") {
        toast.success("Registration cancelled");
        setIsRegistered(false);
        fetchSessionDetails();
      }
    } catch (err) {
      console.error("Cancellation failed:", err);
      toast.error("Failed to cancel registration");
    } finally {
      setRegistering(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader className="w-8 h-8 animate-spin text-uzima-maroon-500" />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">Session not found</p>
      </div>
    );
  }

  const availableSlots = session.maxParticipants - session.participants.length;
  const isFull = availableSlots === 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-12 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Sessions
        </button>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Hero Section */}
          <div className="bg-gradient-to-r from-uzima-maroon-600 to-uzima-maroon-700 p-8 text-white">
            <div className="inline-block px-4 py-2 bg-white/20 rounded-full text-sm font-semibold mb-4">
              {session.category.replace("-", " ").toUpperCase()}
            </div>
            <h1 className="text-4xl font-bold mb-4">{session.title}</h1>
            <p className="text-blue-100 text-lg">{session.topic}</p>
          </div>

          {/* Content */}
          <div className="p-8">
            {/* Session Details Grid */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Calendar className="w-6 h-6 text-uzima-maroon-500 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-600">Date</p>
                    <p className="font-semibold text-gray-900">
                      {new Date(session.sessionDate).toLocaleDateString(
                        "en-US",
                        {
                          weekday: "long",
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        }
                      )}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Clock className="w-6 h-6 text-uzima-maroon-500 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-600">Time</p>
                    <p className="font-semibold text-gray-900">
                      {session.startTime} - {session.endTime}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  {session.sessionType === "virtual" ? (
                    <>
                      <Video className="w-6 h-6 text-uzima-maroon-500 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-600">Format</p>
                        <p className="font-semibold text-gray-900">
                          Virtual Session
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <MapPin className="w-6 h-6 text-uzima-maroon-500 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-600">Location</p>
                        <p className="font-semibold text-gray-900">
                          {session.location}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Users className="w-6 h-6 text-uzima-maroon-500 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-600">Participants</p>
                    <p className="font-semibold text-gray-900">
                      {session.participants.length} / {session.maxParticipants}{" "}
                      registered
                    </p>
                    {!isFull && (
                      <p className="text-sm text-green-600 font-semibold mt-1">
                        {availableSlots} spots available
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <User className="w-6 h-6 text-uzima-maroon-500 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-600">Facilitator</p>
                    <p className="font-semibold text-gray-900">
                      {session.facilitator.fullName}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                About This Session
              </h2>
              <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                {session.description}
              </p>
            </div>

            {/* Registration Status */}
            {isRegistered && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-green-50 border-2 border-green-200 rounded-xl flex items-center        
  gap-3"
              >
                <CheckCircle className="w-6 h-6 text-green-600" />
                <div className="flex-1">
                  <p className="font-semibold text-green-900">
                    You're registered for this session!
                  </p>
                  <p className="text-sm text-green-700">
                    You'll receive a reminder before the session starts.
                  </p>
                </div>
              </motion.div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-4">
              {!isRegistered ? (
                <button
                  onClick={handleRegister}
                  disabled={registering || isFull}
                  className={`flex-1 py-4 rounded-xl font-semibold text-lg transition flex items-center       
  justify-center gap-2 ${
    isFull
      ? "bg-gray-200 text-gray-500 cursor-not-allowed"
      : "bg-gradient-to-r from-uzima-maroon-600 to-uzima-maroon-700 text-white hover:shadow-xl"
  } disabled:opacity-50`}
                >
                  {registering ? (
                    <>
                      <Loader className="w-6 h-6 animate-spin" />
                      Registering...
                    </>
                  ) : isFull ? (
                    "Session Full"
                  ) : (
                    <>
                      <CheckCircle className="w-6 h-6" />
                      Register for Session
                    </>
                  )}
                </button>
              ) : (
                <button
                  onClick={handleCancelRegistration}
                  disabled={registering}
                  className="flex-1 py-4 rounded-xl font-semibold text-lg border-2 border-red-300 
  text-red-600 hover:bg-red-50 transition disabled:opacity-50"
                >
                  {registering ? "Cancelling..." : "Cancel Registration"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupSessionDetails;
